// tslint:disable: cyclomatic-complexity

import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Redirect, withRouter, RouteComponentProps } from 'react-router-dom';
import LoginSSO from '../LoginSSO';
import { Entry } from '../../config';
import { loginRequest } from '../../authConfig';
import { Button, CircularProgress } from '@material-ui/core';
import TextField from '@markinson/uicomponents-v2/TextFieldV1';
import logo from '../../assets/mopro-orange-logo.png';
import * as styles from './Login.scss';

export interface ILoginFields {
  staffUserName: string;
  staffPassword: string;
  msal_token?: any;
}

interface ILoginProperties extends RouteComponentProps {
  loggedIn: boolean;
  isAdmin?: boolean;
  isLoading: boolean;
  error: string;
  login(fields: ILoginFields): void;
}

const getMicrosoftButtonStyle = (isEnabled: boolean) => (isEnabled ? styles.microsoftButton : styles.microsoftButtonDisabled);
const getMicrosoftSVGStyle = (isEnabled: boolean) => (isEnabled ? styles.microsoftIcon : styles.microsoftIconDisabled);

const Login: React.FC<ILoginProperties> = (props) => {
  const { loggedIn, isAdmin, isLoading, error, login, history, location } = props;
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isV3 = (window as any).MomentumProConfiguration?.MPVersion === 'V3';
  const isSSOEnabled = (window as any).MomentumProConfiguration?.EnableSingleSignOn === 'true' && isV3;

  const [fields, setFields] = useState<ILoginFields>({
    staffUserName: isSSOEnabled ? 'Sign in with Microsoft to continue' : '',
    staffPassword: '',
  });
  const userFieldRef = useRef<HTMLInputElement>(null);

  // Set document title
  useEffect(
    () => {
      document.title = 'MomentumPro';
    },
    []
  );

  // Acquire token silently if MSAL account exists
  useEffect(
    () => {
      if (accounts.length > 0) {
        instance
          .acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          })
          .then(() => {
            console.log('Token acquired silently');
          });
      }
    },
    [accounts, instance]
  );

  // Watch for MSAL account changes and update username field
  useEffect(
    () => {
      if (!isSSOEnabled) return;
      if (isAuthenticated && accounts.length > 0) {
        const account = accounts[0];
        setFields((prev) => ({
          ...prev,
          staffUserName: account.username || '',
        }));
      } else {
        setFields((prev) => ({
          ...prev,
          staffUserName: 'Sign in with Microsoft to continue',
        }));
      }
    },
    [isSSOEnabled, isAuthenticated, accounts]
  );

  const handleSubmit = useCallback(
    (loginFields: ILoginFields) => {
      login(loginFields);
    },
    [login]
  );

  const getRedirectRoute = useCallback(
    (): { pathname: string; search?: string } => {
      if (history.location.state && (history.location.state as any).from) {
        const { pathname, search } = (history.location.state as any).from;

        return { pathname, search };
      }

      if (Entry && Entry === 'security-management') {
        return { pathname: '/security-policies' };
      }

      return { pathname: '/dashboard' };
    },
    [history.location.state]
  );

  const route = getRedirectRoute();

  if (
    (Entry && Entry === 'security-management' && loggedIn && isAdmin) ||
    ((!Entry || Entry !== 'security-management') && loggedIn)
  ) {
    return (
      <Redirect
        to={{
          ...route,
          state: { from: location },
        }}
      />
    );
  }

  return (
    <div className={styles.loginContainer}>
      {!isSSOEnabled && (
        <div>
          <LoginSSO
            isLoading={isLoading}
            onSubmit={handleSubmit}
            error={
              error ||
              ((Entry && Entry === 'security-management' && loggedIn && !isAdmin) &&
                'Please sign in as an administrator.')
            }
          />
          {/* Divider */}
          {
            isV3 && (
              <div className={styles.divider}>
                <span className={styles.line}></span>
                <span>OR</span>
                <span className={styles.line}></span>
              </div>
            )
          }
        </div>
      )}

      {isSSOEnabled && (
        <>
          <div>
            <img className={styles.logo} src={logo} alt='logo' />
          </div>
          <div className={styles.usernameFieldContainer}>
            <TextField
              name='staffUserName'
              label='Username'
              value={fields.staffUserName}
              required={true}
              placeholder='Username'
              fieldRef={(el) => (userFieldRef.current = el)}
              disabled={true}
              size={'large'}
            />
          </div>
        </>
      )}

      <div>
        {!loggedIn && isV3 && (
          <div className='collapse navbar-collapse justify-content-end'>
            <Button
              disabled={!isSSOEnabled}
              className={getMicrosoftButtonStyle(isSSOEnabled)}
              variant={'contained'}
              onClick={async () => {
                if (accounts.length > 0) {
                  // Already signed in with MS → just get token silently
                  try {
                    const response = await instance.acquireTokenSilent({
                      ...loginRequest,
                      account: accounts[0],
                    });
                    console.log('Silent token:', response.accessToken);

                    // Call Redux login with the token (instead of username/password)
                    props.login({
                      staffUserName: '',
                      staffPassword: '',
                      msal_token: response.accessToken,
                    });
                  } catch (err) {
                    console.warn('Silent token failed, fallback to redirect', err);
                    instance.loginRedirect(loginRequest);
                  }
                } else {
                  instance.loginRedirect(loginRequest);
                }
              }}
            >
              <>
                <svg
                  aria-hidden='true'
                  viewBox='0 0 25 25'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className={getMicrosoftSVGStyle(isSSOEnabled)}
                >
                  <path d='M11.5216 0.5H0V11.9067H11.5216V0.5Z' fill='#f25022'></path>
                  <path d='M24.2418 0.5H12.7202V11.9067H24.2418V0.5Z' fill='#7fba00'></path>
                  <path d='M11.5216 13.0933H0V24.5H11.5216V13.0933Z' fill='#00a4ef'></path>
                  <path d='M24.2418 13.0933H12.7202V24.5H24.2418V13.0933Z' fill='#ffb900'></path>
                </svg>
              </>
              Sign in with Microsoft
              {isLoading && isSSOEnabled && (
                <CircularProgress
                  size={'24px'}
                  className={styles.progressIndicator}
                />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default withRouter(Login);
